import { Flex } from '@fluentui/react-northstar';
import React from 'react';
import { NestedComponent } from '../../browser-pages/browser-components/NestedComponent';
import { NodeType } from '../../shared/cache/NodeCache';
import { TicketConversation } from '../../shared/components/TicketConversation/TicketConversation';
import { TicketCustomFormSection } from '../../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { TicketLifecycleSection } from '../../shared/components/TicketLifecycleTab/TicketLifecycleSection';
import { IResolution } from '../../shared/components/TicketResolutionNoteComponents/ResolutionHelper';
import { ResolutionNote } from '../../shared/components/TicketResolutionNoteComponents/ResolutionNote';
import { TicketTimeTracking } from '../../shared/components/TicketTimeTracking/TicketTimeTracking';
import { ADMINISTRATORS } from '../../shared/utils/constants';
import { ACTIONS, EditTicketState } from './editTicketState';
import { MenuItem } from './hooks/useTicketMenuItems';

interface TicketTabsContentProps {
  activeMenuItem: MenuItem | undefined;
  state: EditTicketState;
  ticketId: any;
  isUserLicensed: boolean;
  isResolutionNoteEnabled: boolean;
  tikitConfig: TikitConfiguration;
  currentState: any;
  globalTheme: any;
  droppedIndex: number;
  reloadConversation: boolean;
  uploadedFiles: any[];
  dispatch: React.Dispatch<any>;
  setAppState: (updater: any) => void;
  setUploadedFiles: React.Dispatch<React.SetStateAction<any[]>>;
  setReloadConversation: React.Dispatch<React.SetStateAction<boolean>>;
  onCommentUpdated?: () => void;
  resetCommentsCount: () => void;
  onSendComment: (date: Date) => Promise<void> | void;
  onTicketUpdate: (data: any, date: Date) => Promise<void> | void;
  onReloadPhases: () => Promise<void> | void;
  onToolbarInteraction: (action: any) => Promise<void> | void;
  saveComplete: (action: any) => Promise<void> | void;
}

/**
 * Renders the active tab's content for the EditTicket center pane.
 * Switches on `activeMenuItem.key`: activity / lifecycle / customForm /
 * resolution / node-* (custom node tabs).
 */
const TicketTabsContentImpl: React.FC<TicketTabsContentProps> = ({
  activeMenuItem,
  state,
  ticketId,
  isUserLicensed,
  isResolutionNoteEnabled,
  tikitConfig,
  currentState,
  globalTheme,
  droppedIndex,
  reloadConversation,
  uploadedFiles,
  dispatch,
  setAppState,
  setUploadedFiles,
  setReloadConversation,
  onCommentUpdated,
  resetCommentsCount,
  onSendComment,
  onTicketUpdate,
  onReloadPhases,
  onToolbarInteraction,
  saveComplete
}) => {
  // Declared above the early return so it stays unconditional, and memoised so the memoised
  // time tracking tab is not re-rendered by a fresh arrow on every parent render.
  //
  // The modified stamp is taken here rather than read back from the server, matching what
  // onTicketUpdate already does for an ordinary save. The server sets its own stamp in the
  // same transaction; this is the client's approximation of it until the next load.
  //
  // Only when this page did the changing. A load that picks up another agent's time moves
  // the total and leaves the stamp alone: the ticket was modified, but not at a moment this
  // page can know, and stamping now would report "modified" for opening a tab. The header's
  // Modified value stays at the one it loaded with until a real reload - deliberately, so a
  // fresh total sits beside an honestly stale timestamp rather than an invented one.
  const handleTotalAgentTimeChanged = React.useCallback(
    (totalMinutes: number, changedHere: boolean) =>
      dispatch({
        type: ACTIONS.SET_TOTAL_AGENT_TIME,
        data: { totalMinutes, modifiedDate: changedHere ? new Date().toISOString() : undefined }
      }),
    [dispatch]
  );

  if (!activeMenuItem) return null;

  const isReady = !state.isGettingSignedInUser && !state.isGettingRequester;
  const isDropzoneDisabled =
    state?.fileTriggerChannel?.TriageChannel == null ||
    state?.fileTriggerChannel?.TriageChannel?.length < 1 ||
    state?.ticket == null ||
    state?.ticket?.Closed;

  switch (activeMenuItem.key) {
    case 'activity':
      if (!isReady) return null;
      return (
        <TicketConversation
          isDropzoneDisabled={isDropzoneDisabled}
          status={state.ticket.StatusId}
          dueDate={state.ticket.DueDate}
          priority={state.ticket.PriorityId}
          teamId={state.ticket.TeamId}
          groupId={state.ticket.SupportGroupId}
          isTicketMerged={state.ticket.IsMerged}
          isTicketClosed={state.ticket.Closed}
          isUserLicensed={isUserLicensed}
          ticketId={ticketId}
          assigneeId={state.ticket.AssigneeId}
          requester={state.requester}
          signedInUser={state.signedInUser}
          isEndUserPage={false}
          onTicketUpdate={onTicketUpdate}
          resetCommentsCount={resetCommentsCount}
          onSendComment={onSendComment}
          ticketData={state.ticket}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          reloadConversation={reloadConversation}
          setReloadConversation={setReloadConversation}
          onCommentUpdated={onCommentUpdated}
          globalTheme={globalTheme}
          droppedIndex={droppedIndex}
          formState={state}
          dispatch={dispatch}
          actions={ACTIONS}
          resolutionNoteConfig={tikitConfig}
          isResolutionNoteEnabled={isResolutionNoteEnabled}
          groupChat={state.ticket.GroupChat}
        />
      );

    case 'lifecycle':
      if (!isReady) return null;
      return (
        <TicketLifecycleSection
          ticketData={state.ticket}
          ticketLifecycle={state.ticket.TicketLifecycle}
          data={currentState}
          onReloadPhases={onReloadPhases}
        />
      );

    case 'customForm':
      if (!isReady) return null;
      return (
        <Flex column gap="gap.small" className={'mb-5 overflow-auto'}>
          <TicketCustomFormSection
            customForms={state.ticket.CustomForms}
            showUpdateAnswerButton={true}
            ticketId={state.ticket.Id}
            setAppStateEvent={setAppState}
            dispatchEvent={dispatch}
            onInteraction={saveComplete}
            template={state.ticket.Template}
          />
        </Flex>
      );

    case 'resolution':
      return (
        <ResolutionNote
          resolutionProp={
            {
              ticketData: state.ticket,
              onToolbarInteraction: onToolbarInteraction,
              resolutionNoteConfig: tikitConfig
            } as IResolution
          }
          dispatch={dispatch}
        />
      );

    case 'timeTracking':
      if (!isReady) return null;
      return (
        <TicketTimeTracking
          ticketId={state.ticket.Id}
          currentUserId={currentState?.currentUserId}
          isAdmin={currentState?.userRoles?.roles?.includes(ADMINISTRATORS) ?? false}
          isTicketClosed={state.ticket.Closed}
          onTotalChanged={handleTotalAgentTimeChanged}
        />
      );

    default:
      if (activeMenuItem.key.startsWith('node-')) {
        return (
          <NestedComponent
            node={activeMenuItem.nodeId}
            type={NodeType.Tab}
            data={{ ticketId: ticketId, requesterEntra: state.requester.AadObjectId }}
          />
        );
      }
      return null;
  }
};

export const TicketTabsContent = React.memo(TicketTabsContentImpl);
